import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';

// User data selectors
export const selectUser = (state: RootState) => state.auth.user;
export const selectUserRole = (state: RootState) => state.auth.user?.role || null;
export const selectUserId = (state: RootState) => state.auth.user?.userId || state.auth.user?.id || null;
export const selectUserEmail = (state: RootState) => state.auth.user?.email || null;
export const selectUserName = (state: RootState) => state.auth.user?.name || null;
export const selectClientId = (state: RootState) => state.auth.user?.clientId || null;
export const selectWorkspaceId = (state: RootState) => state.auth.user?.workspaceId || state.auth.user?.defaultWorkspaceId || null;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectLogoutLoading = (state: RootState) => state.auth.isLoggingOut; // Simple logout loading selector
export const selectAuthError = (state: RootState) => state.auth.error;

// Role-based selectors with enhanced debugging
export const selectIsAdmin = (state: RootState) => {
    const role = selectUserRole(state);
    const isAdmin = role === 'ORGANIZATION_ADMIN' || role === 'WORKSPACE_ADMIN' || role === 'SUPER_ADMIN';
    // console.log('🔍 selectIsAdmin:', { role, isAdmin });
    return isAdmin;
};

export const selectIsAgent = (state: RootState) => {
    const role = selectUserRole(state);
    const isAgent = role === 'WORKSPACE_AGENT';
    // console.log('🔍 selectIsAgent:', { role, isAgent });
    return isAgent;
};

export const selectIsOrganizationAdmin = (state: RootState) => {
    const role = selectUserRole(state);
    return role === 'ORGANIZATION_ADMIN';
};

export const selectIsWorkspaceAdmin = (state: RootState) => {
    const role = selectUserRole(state);
    return role === 'WORKSPACE_ADMIN';
};

// User context for permissions - memoized to prevent unnecessary re-renders
export const selectUserContext = createSelector(
    [selectUserId, selectClientId, selectWorkspaceId],
    (userId, clientId, workspaceId) => ({
        userId,
        clientId,
        workspaceId,
    })
);

// Debug selector to see full auth state
export const selectAuthDebug = (state: RootState) => ({
    isAuthenticated: state.auth.isAuthenticated,
    user: state.auth.user,
    loading: state.auth.loading,
    error: state.auth.error,
    permissions: state.auth.permissions
}); 